/**
 * Which app flow a blog post's in-content CTA points at.
 *
 * Extracted verbatim from components/blog/contextual-tool-cta.tsx so SERVER code
 * can ask the same question the client component answers. The render-time prose
 * CTA pass (lib/internal-links.ts, via lib/content-processor.ts) needs it to know
 * whether a post already offers an in-place grass-ID uploader; a duplicated copy
 * of this routing would drift the moment either side gained a branch.
 *
 * Icons are the ONLY thing that stayed behind: this module returns an icon KEY
 * and the client component maps it to a Lucide component, so no React icon
 * package is pulled into the server content pipeline.
 */
import {
  isDiagnosisPost,
  DIAGNOSE_CTA,
} from './blog-diagnose-cta'
import { IDENTIFY_CTA } from './identify-cta'
import { isWeedPost, weedIdentifierHref, WEED_IDENTIFIER_CTA } from './weed-identifier-cta'
import { isGrassIdentityIntent } from './blog-intent'

/** Icon identity, resolved to a Lucide component by the client component. */
export type ToolIconKey = 'scan' | 'bug' | 'package' | 'droplets' | 'stethoscope' | 'layers'

export interface ContextualTool {
  href: string
  label: string
  tagline: string
  icon: ToolIconKey
}

export function resolveContextualTool(slug?: string, category?: string, tags?: string[]): ContextualTool | null {
  const s = slug?.toLowerCase() || ''
  const c = category?.toLowerCase() || ''

  // Weed & feed posts (Scotts or not) get the dedicated Weed & Feed Calculator,
  // which answers timing + amount AND runs the grass-safety check inline. Matched
  // BEFORE the generic Scotts grass-ID rule so these posts reach the exact-match
  // tool. Deep-link the product when the slug names one.
  if (s.includes('weed') && s.includes('feed')) {
    let product = ''
    if (s.includes('triple-action')) product = 'ta'
    else if (s.includes('bonus')) product = 'bonus'
    else if (s.includes('centipede')) product = 'fert'
    else if (s.includes('bermuda')) product = 'wf3'
    return {
      href: `/tools/weed-and-feed-calculator${product ? `?product=${product}` : ''}`,
      label: 'Weed & Feed Calculator',
      tagline: 'When to apply and how much to buy for your ZIP and lawn size',
      icon: 'bug',
    }
  }

  // Dethatch / thatch / power-rake posts route to the Dethatching Calculator
  // (dethatch-or-wait verdict + method + timing window). Matched BEFORE the
  // generic Scotts branch and the aeration/grass-type branches so an
  // exact-match post like "how-to-dethatch-a-lawn" or a hypothetical
  // "scotts-dethatching-guide" reaches the specific tool, not grass-ID.
  if (s.includes('dethatch') || s.includes('thatch') || s.includes('power-rake') || s.includes('verticut')) {
    return {
      href: '/tools/dethatching-calculator',
      label: 'Dethatching Calculator',
      tagline: 'See if your lawn needs dethatching, plus the right tool and timing for your ZIP',
      icon: 'layers',
    }
  }

  // Scotts traffic (~3,300 clicks/mo across 10+ posts) is high-intent but
  // intent-adjacent — readers are picking a product, not yet committed. Push
  // grass-ID first because Scotts product/timing depends on warm vs cool
  // season. Matched before the weed-control rule so Scotts posts override
  // the herbicide-timing default.
  if (s.includes('scotts')) {
    return {
      href: '/#analysis-card',
      label: 'Identify your grass first',
      tagline: 'Scotts timing depends on warm vs cool season. Find out in 10s.',
      icon: 'scan',
    }
  }

  // Grub / white-grub / beetle-larvae posts get the Grub Control Calculator
  // (preventive vs curative timing + how much). Matched before the generic
  // weed-control rule so pest posts don't fall through to herbicide timing.
  if (s.includes('grub') || s.includes('white-grub') || (c === 'pest control' && s.includes('grub'))) {
    return { href: '/tools/grub-control-calculator', label: 'Grub Control Calculator', tagline: 'Preventive vs curative timing and how much to buy for your ZIP', icon: 'bug' }
  }

  // Supported-weed posts enter the dedicated photo identifier even when the
  // article taxonomy also classifies them as a generic lawn problem.
  if (isWeedPost(slug, category, tags)) {
    return {
      href: weedIdentifierHref('blog_inline', slug),
      label: WEED_IDENTIFIER_CTA.label,
      tagline: WEED_IDENTIFIER_CTA.tagline,
      icon: 'bug',
    }
  }

  // Symptom/problem posts (disease, fungus, non-grub pest damage, discoloration)
  // route to the photo-based /diagnose flow — the highest-converting capture
  // surface on the site (25% vs ~1-3%; July 2026 audit). Matched after the
  // dedicated grub/weed-feed/scotts calculators so those keep their better tools,
  // and BEFORE the generic weed/fertilizer/grass-type branches so a disease post
  // about a named grass ("st augustine brown patch") reaches diagnose, not grass-ID.
  if (isDiagnosisPost(slug, category, tags)) {
    return {
      href: DIAGNOSE_CTA.href,
      label: DIAGNOSE_CTA.label,
      tagline: DIAGNOSE_CTA.tagline,
      icon: 'stethoscope',
    }
  }

  if (s.includes('weed') || s.includes('herbicide') || s.includes('pre-emergent') || s.includes('crabgrass') || s.includes('preventer') || c === 'weed control') {
    return { href: '/tools/herbicide-timing', label: 'Herbicide Timing Calculator', tagline: 'Find the best time to apply weed control in your area', icon: 'bug' }
  }
  if (s.includes('fertiliz') || s.includes('10-10-10') || s.includes('winterizer') || s.includes('winter-lawn') || s.includes('winter') || c === 'fertilizing') {
    return { href: '/tools/fertilizer-calculator', label: 'Fertilizer Calculator', tagline: 'Calculate exactly how much fertilizer your lawn needs', icon: 'package' }
  }
  if (s.includes('water') || s.includes('irrigation') || s.includes('drought') || c === 'watering') {
    return { href: '/tools/watering-schedule', label: 'Watering Schedule Generator', tagline: 'Get a personalized watering schedule for your lawn', icon: 'droplets' }
  }
  // Token list lives in lib/blog-intent.ts so the post-read slot agrees with
  // this one about what counts as an identify post.
  if (isGrassIdentityIntent(s, c)) {
    return { href: '/#analysis-card', label: IDENTIFY_CTA.label, tagline: 'Upload a photo and get a personalized care plan', icon: 'scan' }
  }

  return null
}

/**
 * True when the post's in-content CTA is ALREADY the in-place grass-ID uploader
 * (the default no-contextual-tool case, the Scotts reframe, and explicit
 * grass-identity intent all render an InlineScanCta into the grass-ID flow).
 *
 * Used to decide that a prose autolink to the same destination is a second,
 * weaker CTA on that post rather than the reader's only route in.
 */
export function rendersGrassIdUploader(
  slug?: string,
  category?: string,
  tags?: string[],
): boolean {
  const tool = resolveContextualTool(slug, category, tags)
  return tool === null || tool.href === IDENTIFY_CTA.href
}
